import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import {
  Zap, ArrowRight, CheckCircle2, ShoppingBag, Globe, Palette, CreditCard, BarChart3,
  ShieldCheck, Layers, Rocket, Users, Package, Crown, Store, ChevronDown, Sparkles,
  TrendingUp, Monitor, Tablet, Smartphone, Moon, Sun, Menu, X, Search, TicketPercent,
  Server, Paintbrush, Link2, Bell, IndianRupee, Heart, MousePointerClick, BadgeCheck, Play,
} from "lucide-react";
import api, { fmtINR } from "../lib/api";
import BrandLogo from "../components/BrandLogo";
import { Reveal, Marquee } from "../components/Shared";
import PresetSwitcher, { applyPreset } from "../components/PresetSwitcher";
import { useAuth, DASHBOARD_HOME } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PricingCards, PricingCalculator } from "../components/PricingPlans";
import SuperAdminSection from "../components/SuperAdminSection";
import { AdSlot, AdNetworkScripts } from "../ads/AdEngine";
import { applySeo } from "../lib/seo";
import { initPixel, initGa4, track, captureUtm } from "../lib/tracking";

const WORDS = ["Build.", "Launch.", "Sell.", "Grow."];

const FLOW = [
  { label: "Super Admin", icon: Crown },
  { label: "Partner", icon: Users },
  { label: "Create Website", icon: MousePointerClick },
  { label: "Select Theme", icon: Palette },
  { label: "Business Owner", icon: Store },
  { label: "Online Store", icon: ShoppingBag },
  { label: "Customers", icon: Heart },
];

const STEPS = [
  { n: "01", title: "Create Your Account", body: "Sign up and get instant access to the website builder console.", icon: Users },
  { n: "02", title: "Create a Website", body: "A 5-step wizard takes you from idea to live website in under two minutes.", icon: MousePointerClick },
  { n: "03", title: "Choose a Premium Theme", body: "15+ professionally designed themes across e-commerce, business, restaurant and more.", icon: Palette },
  { n: "04", title: "Hand It to Your Client", body: "Every website gets its own store admin login — your client manages products and orders.", icon: Store },
  { n: "05", title: "Grow Your Business", body: "Charge clients your own price. Pay only a flat per-website subscription to MILTROO.", icon: TrendingUp },
];

const FEATURES = [
  { icon: Layers, title: "Multi-Tenant SaaS", body: "Complete data isolation between partners, stores and customers." },
  { icon: MousePointerClick, title: "Website Builder", body: "5-step wizard. Zero code. Instant launch." },
  { icon: ShoppingBag, title: "E-Commerce", body: "Products, variants, inventory, cart, coupons and checkout." },
  { icon: Paintbrush, title: "Premium Themes", body: "15+ responsive themes with full color customization." },
  { icon: Link2, title: "Custom Domains", body: "Connect any domain with automatic HTTPS." },
  { icon: Package, title: "Product Management", body: "SKUs, stock, categories, galleries and pricing." },
  { icon: ShoppingBag, title: "Order Management", body: "Real-time order tracking and status workflows." },
  { icon: Users, title: "Customer Management", body: "Automatic customer profiles with lifetime value." },
  { icon: BarChart3, title: "Analytics", body: "Revenue charts, best sellers and growth insights." },
  { icon: CreditCard, title: "Subscription Billing", body: "Tiered per-website pricing, invoices and renewals." },
  { icon: IndianRupee, title: "PhonePe Payments", body: "UPI-native checkout with webhook verification." },
  { icon: Server, title: "Secure Hosting", body: "Role-based access, audit logs and encrypted auth." },
  { icon: Smartphone, title: "Mobile Responsive", body: "Every site and dashboard is mobile-first." },
  { icon: Moon, title: "Dark Mode", body: "Beautiful dark and light themes everywhere." },
  { icon: Bell, title: "Notifications", body: "Orders, payments and subscription alerts in real time." },
];

/* ---------- Hero dashboard mock ---------- */
function DashboardMock() {
  return (
    <div className="relative" data-testid="hero-mock">
      <motion.div initial={{ opacity: 0, y: 40, rotate: 2 }} animate={{ opacity: 1, y: 0, rotate: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
        className="glass-card rounded-2xl overflow-hidden shadow-2xl glow">
        <div className="flex items-center gap-1.5 px-4 py-3 border-b border-black/5 dark:border-white/10">
          <span className="h-2.5 w-2.5 rounded-full bg-red-400" /><span className="h-2.5 w-2.5 rounded-full bg-amber-400" /><span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
          <span className="ml-3 text-[10px] font-bold text-muted-foreground tracking-widest uppercase">Partner Dashboard</span>
        </div>
        <div className="grid grid-cols-[56px_1fr]">
          <div className="border-r border-black/5 dark:border-white/10 p-2.5 space-y-2.5">
            {[Globe, Palette, ShoppingBag, BarChart3].map((I, i) => (
              <div key={i} className={`h-8 w-8 rounded-lg flex items-center justify-center ${i === 0 ? "gradient-bg text-white" : "bg-muted/60 text-muted-foreground"}`}><I className="h-3.5 w-3.5" /></div>
            ))}
          </div>
          <div className="p-4 space-y-3">
            <div className="flex gap-3">
              {[["Websites", "30"], ["Revenue", "₹1.2L"], ["Orders", "486"]].map(([l, v]) => (
                <div key={l} className="flex-1 rounded-lg border border-black/5 dark:border-white/10 p-3 bg-white/40 dark:bg-white/5">
                  <p className="text-[9px] uppercase tracking-widest text-muted-foreground font-bold">{l}</p>
                  <p className="font-display font-extrabold text-lg tracking-tight">{v}</p>
                </div>
              ))}
            </div>
            <div className="rounded-lg border border-black/5 dark:border-white/10 p-3 bg-white/40 dark:bg-white/5 h-24 flex items-end gap-2">
              {[40, 65, 50, 80, 62, 92, 74, 100, 85, 96].map((h, i) => (
                <motion.div key={i} initial={{ scaleY: 0.1 }} animate={{ scaleY: 1 }} transition={{ delay: 0.5 + i * 0.07, duration: 0.5 }}
                  className="flex-1 rounded-t-sm origin-bottom gradient-bg" style={{ height: `${h}%`, opacity: 0.35 + (h / 100) * 0.65 }} />
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Floating cards */}
      <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.9 }}
        className="absolute -left-6 -top-6 glass-card rounded-xl px-4 py-3 shadow-xl animate-float flex items-center gap-3">
        <div className="h-8 w-8 rounded-full bg-emerald-500/15 text-emerald-500 flex items-center justify-center"><ShoppingBag className="h-4 w-4" /></div>
        <div><p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">New order</p><p className="text-sm font-bold">₹4,299 · StyleHub</p></div>
      </motion.div>
      <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.15 }}
        className="absolute -right-4 top-1/3 glass-card rounded-xl px-4 py-3 shadow-xl animate-float-slow flex items-center gap-3">
        <div className="h-8 w-8 rounded-full gradient-bg text-white flex items-center justify-center"><CreditCard className="h-4 w-4" /></div>
        <div><p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Payment</p><p className="text-sm font-bold">Successful ✓</p></div>
      </motion.div>
      <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.4 }}
        className="absolute -left-4 -bottom-6 glass-card rounded-xl px-4 py-3 shadow-xl animate-float-x flex items-center gap-3">
        <div className="h-8 w-8 rounded-full bg-blue-500/15 text-blue-500 flex items-center justify-center"><Rocket className="h-4 w-4" /></div>
        <div><p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Website created</p><p className="text-sm font-bold">brewbean.in is live</p></div>
      </motion.div>
    </div>
  );
}

function RevenueCalculator() {
  const [sites, setSites] = useState(30);
  const [charge, setCharge] = useState(5000);
  const yearly = sites * charge;
  return (
    <div className="glass-card rounded-2xl p-8 sm:p-10" data-testid="revenue-calculator">
      <p className="overline-label">Illustrative partner revenue</p>
      <div className="grid sm:grid-cols-2 gap-8 mt-6">
        <div>
          <div className="flex justify-between text-sm font-bold mb-3"><span>Websites you manage</span><span className="font-display font-extrabold text-lg">{sites}</span></div>
          <input data-testid="rev-sites-slider" type="range" min="1" max="100" value={sites} onChange={(e) => setSites(Number(e.target.value))} className="w-full h-2 rounded-full appearance-none cursor-pointer accent-[var(--p2)]" style={{ background: `linear-gradient(90deg, var(--p2), var(--p3))` }} />
          <div className="mt-6">
            <label className="overline-label">You charge each client (₹/year)</label>
            <Input data-testid="rev-charge-input" type="number" value={charge} onChange={(e) => setCharge(Number(e.target.value) || 0)} className="mt-2 h-12 rounded-md" />
          </div>
        </div>
        <div className="flex flex-col justify-center">
          <p className="text-xs text-muted-foreground">Potential client billing</p>
          <motion.p key={yearly} initial={{ scale: 0.92, opacity: 0.5 }} animate={{ scale: 1, opacity: 1 }}
            data-testid="rev-yearly" className="font-display font-extrabold text-5xl tracking-tighter text-gradient">{fmtINR(yearly)}</motion.p>
          <p className="text-xs text-muted-foreground mt-1">per year</p>
          <p className="text-xs text-muted-foreground mt-4 max-w-xs leading-relaxed">
            Illustration only — you set your own client pricing. MILTROO charges a flat per-website subscription. No income is guaranteed.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ---------- Main page ---------- */
export default function Home() {
  const [pricing, setPricing] = useState(null);
  const [themes, setThemes] = useState([]);
  const [word, setWord] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const [device, setDevice] = useState("desktop");
  const [mobileNav, setMobileNav] = useState(false);
  const [preset, setPreset] = useState(localStorage.getItem("zylo_preset") || "");
  const { user } = useAuth();
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  useEffect(() => {
    if (preset) { document.documentElement.dataset.preset = preset; }
    else {
      api.get("/public/pricing").then((r) => {
        setPricing(r.data);
        if (!localStorage.getItem("zylo_preset")) document.documentElement.dataset.preset = r.data.preset || "aurora";
      });
      return;
    }
    api.get("/public/pricing").then((r) => setPricing(r.data));
  }, []); // eslint-disable-line
  useEffect(() => {
    captureUtm();
    api.get("/public/seo/platform").then((r) => {
      applySeo(r.data);
      if (r.data.pixel_id) initPixel(r.data.pixel_id);
      if (r.data.ga4_id) initGa4(r.data.ga4_id);
      track("page_view", {}, { scope: "platform" });
    }).catch(() => {});
  }, []);
  useEffect(() => { api.get("/public/themes").then((r) => setThemes(r.data)); }, []);
  useEffect(() => {
    const t = setInterval(() => setWord((w) => (w + 1) % WORDS.length), 2200);
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { clearInterval(t); window.removeEventListener("scroll", onScroll); };
  }, []);

  const pricingCfg = pricing?.pricing || null;

  const navLinks = [["Features", "#features"], ["Themes", "#themes"], ["How It Works", "#how"], ["Pricing", "#pricing"]];
  const marketplaceLink = <Link to="/marketplace" data-testid="nav-marketplace"
    className="px-3.5 py-2 rounded-full text-sm font-bold text-gradient hover:bg-muted/60 transition-colors">Marketplace</Link>;
  const mplayLink = <Link to="/videos" data-testid="nav-mplay"
    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-extrabold text-emerald-600 hover:bg-emerald-500/10 transition-colors">
    <span className="h-5 w-5 rounded-md flex items-center justify-center" style={{ background: "linear-gradient(135deg,#4ade80,#16a34a)" }}>
      <Play className="h-3 w-3 text-white fill-white ml-px" />
    </span>M Play</Link>;

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden" data-testid="homepage">
      {/* ===== NAV ===== */}
      <nav className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "glass-header shadow-lg shadow-black/5" : "bg-transparent"}`}>
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center gap-6">
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <BrandLogo size="md" />
          </Link>
          <div className="hidden lg:flex items-center gap-1 ml-6">
            {navLinks.map(([l, h]) => (
              <a key={l} href={h} data-testid={`nav-${l.toLowerCase().replace(/\s+/g, "-")}`}
                className="px-3.5 py-2 rounded-full text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors">{l}</a>
            ))}
            {marketplaceLink}
            {mplayLink}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <PresetSwitcher value={preset || undefined} onChange={(p) => { setPreset(p); applyPreset(p); }} />
            {user ? (
              <Button data-testid="nav-dashboard" onClick={() => navigate(DASHBOARD_HOME[user.role])} className="rounded-full font-bold gradient-bg text-white border-0 glow-sm active:scale-95 transition-transform">
                Open Dashboard <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            ) : (
              <>
                <div className="relative group">
                  <Button variant="ghost" data-testid="nav-login" className="rounded-full font-bold">Login <ChevronDown className="h-3.5 w-3.5 ml-1" /></Button>
                  <div className="absolute right-0 top-full pt-2 hidden group-hover:block">
                    <div className="glass-card rounded-xl shadow-xl p-2 w-52">
                      {[["Partner Login", "/login"], ["Business Owner Login", "/business-owner/login"], ["Super Admin Login", "/super-admin/login"]].map(([l, to]) => (
                        <Link key={to} to={to} data-testid={`nav-login-${l.split(" ")[0].toLowerCase()}`}
                          className="block px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-muted transition-colors">{l}</Link>
                      ))}
                    </div>
                  </div>
                </div>
                <Button data-testid="nav-get-started" onClick={() => navigate("/login")} className="hidden sm:inline-flex rounded-full font-bold gradient-bg text-white border-0 glow-sm active:scale-95 transition-transform">
                  Get Started
                </Button>
              </>
            )}
            <button className="lg:hidden h-10 w-10 rounded-full border border-border flex items-center justify-center" onClick={() => setMobileNav(!mobileNav)} data-testid="nav-mobile-toggle">
              {mobileNav ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>
        {mobileNav && (
          <div className="lg:hidden glass-header border-t border-border px-6 py-4 space-y-1">
            {navLinks.map(([l, h]) => <a key={l} href={h} onClick={() => setMobileNav(false)} className="block px-3 py-2.5 rounded-lg text-sm font-semibold hover:bg-muted">{l}</a>)}
            <Link to="/marketplace" onClick={() => setMobileNav(false)} className="block px-3 py-2.5 rounded-lg text-sm font-bold text-gradient">Marketplace →</Link>
            <Link to="/videos" onClick={() => setMobileNav(false)} data-testid="mnav-mplay" className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-extrabold text-emerald-600">
              <span className="h-5 w-5 rounded-md flex items-center justify-center" style={{ background: "linear-gradient(135deg,#4ade80,#16a34a)" }}><Play className="h-3 w-3 text-white fill-white ml-px" /></span>M Play →</Link>
            <Link to="/login" onClick={() => setMobileNav(false)} className="block px-3 py-2.5 rounded-lg text-sm font-bold text-gradient">Login →</Link>
            <Link to="/login" onClick={() => setMobileNav(false)} className="block px-3 py-2.5 rounded-lg text-sm font-bold text-gradient">Get Started →</Link>
          </div>
        )}
      </nav>

      {/* ===== HERO ===== */}
      <section ref={heroRef} className="relative pt-32 pb-24 sm:pt-40 sm:pb-32 hero-aurora">
        <div className="absolute top-20 left-[8%] h-64 w-64 rounded-full opacity-30 blur-3xl animate-blob" style={{ background: "var(--p1)" }} />
        <div className="absolute top-40 right-[6%] h-72 w-72 rounded-full opacity-25 blur-3xl animate-blob" style={{ background: "var(--p2)", animationDelay: "-5s" }} />
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center relative">
          <motion.div style={{ y: heroY }}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 glass-card rounded-full px-4 py-2 text-xs font-bold">
              <Sparkles className="h-3.5 w-3.5 text-accent" />
              Partner-powered Website & E-Commerce SaaS
            </motion.div>
            <div className="h-16 sm:h-20 mt-6 overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.span key={word} initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -60, opacity: 0 }} transition={{ duration: 0.4 }}
                  className="font-display font-extrabold text-5xl sm:text-7xl tracking-tighter text-gradient inline-block">
                  {WORDS[word]}
                </motion.span>
              </AnimatePresence>
            </div>
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
              className="font-display font-extrabold text-4xl sm:text-6xl tracking-tighter leading-[1.05]">
              Powerful websites & online stores for your clients —{" "}
              <span className="text-gradient">faster.</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
              className="text-muted-foreground text-lg mt-6 max-w-lg leading-relaxed">
              MILTROO lets partners create professional business websites and full e-commerce stores for their clients — no complicated development, no servers, no headaches.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="flex flex-wrap gap-3 mt-8">
              <Button data-testid="hero-get-started" onClick={() => navigate("/login")}
                className="h-13 px-8 py-3.5 rounded-full font-bold text-base gradient-bg text-white border-0 glow active:scale-95 transition-transform">
                Get Started <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <Button data-testid="hero-view-pricing" variant="outline" onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })}
                className="h-13 px-8 py-3.5 rounded-full font-bold text-base glass-card">
                View Pricing
              </Button>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }} className="flex flex-wrap gap-x-6 gap-y-2 mt-8">
              {[`${pricing?.trial_days ?? 14}-day free trial`, "No credit card required", "Cancel anytime"].map((t) => (
                <span key={t} className="flex items-center gap-1.5 text-sm text-muted-foreground"><CheckCircle2 className="h-4 w-4 text-emerald-500" />{t}</span>
              ))}
            </motion.div>
          </motion.div>
          <DashboardMock />
        </div>
      </section>

      <Marquee items={["BUILD", "LAUNCH", "GROW", "SELL", "SCALE"]} />

      {/* ===== FLOW SHOWCASE ===== */}
      <section className="py-24 max-w-7xl mx-auto px-6">
        <Reveal className="text-center">
          <p className="overline-label text-gradient">The ecosystem</p>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter mt-3">One platform. Four levels of growth.</h2>
        </Reveal>
        <div className="flex flex-wrap items-stretch justify-center gap-3 mt-14">
          {FLOW.map(({ label, icon: Icon }, i) => (
            <Reveal key={label} delay={i * 0.08} className="flex items-center gap-3">
              <div data-testid={`flow-${i}`} className="glass-card rounded-xl px-5 py-4 flex items-center gap-3 hover:glow-sm hover:-translate-y-1 transition-all duration-300">
                <div className={`h-9 w-9 rounded-full flex items-center justify-center ${i % 2 === 0 ? "gradient-bg text-white" : "bg-accent/10 text-accent"}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <span className="text-sm font-bold whitespace-nowrap">{label}</span>
              </div>
              {i < FLOW.length - 1 && (
                <motion.span animate={{ x: [0, 5, 0] }} transition={{ repeat: Infinity, duration: 1.6, delay: i * 0.2 }} className="text-accent font-display font-extrabold hidden sm:block">→</motion.span>
              )}
            </Reveal>
          ))}
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section id="how" className="py-24 gradient-bg-soft">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="text-center">
            <p className="overline-label text-gradient">How it works</p>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter mt-3">From signup to live store in minutes</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5 mt-14">
            {STEPS.map(({ n, title, body, icon: Icon }, i) => (
              <Reveal key={n} delay={i * 0.08}>
                <div data-testid={`step-${n}`} className="glass-card rounded-xl p-6 h-full hover:-translate-y-1.5 hover:glow-sm transition-all duration-300 group">
                  <span className="font-display font-extrabold text-4xl text-gradient">{n}</span>
                  <div className="h-11 w-11 rounded-xl gradient-bg text-white flex items-center justify-center mt-5 group-hover:scale-110 transition-transform">
                    <Icon className="h-5 w-5" />
                  </div>
                  <p className="font-display font-bold text-lg mt-4">{title}</p>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== THEME SHOWCASE ===== */}
      <section id="themes" className="py-24 max-w-7xl mx-auto px-6">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="overline-label text-gradient">Theme marketplace</p>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter mt-3">Premium themes for every business</h2>
          </div>
          <div className="flex items-center gap-2 glass-card rounded-full p-1.5">
            {[["desktop", Monitor], ["tablet", Tablet], ["mobile", Smartphone]].map(([d, Icon]) => (
              <button key={d} data-testid={`device-${d}`} onClick={() => setDevice(d)}
                className={`h-9 w-9 rounded-full flex items-center justify-center transition-colors ${device === d ? "gradient-bg text-white" : "text-muted-foreground hover:text-foreground"}`}>
                <Icon className="h-4 w-4" />
              </button>
            ))}
          </div>
        </Reveal>
        <div className="mt-12 flex gap-6 overflow-x-auto pb-6 snap-x" style={{ scrollbarWidth: "thin" }}>
          {themes.map((t, i) => (
            <Reveal key={t.id} delay={Math.min(i * 0.05, 0.4)} className="snap-start shrink-0">
              <div data-testid={`showcase-theme-${t.id}`}
                className="w-72 group rounded-xl overflow-hidden glass-card hover:glow hover:-translate-y-2 transition-all duration-300">
                <div className="relative overflow-hidden bg-muted flex justify-center" style={{ height: 190 }}>
                  <img src={t.preview} alt={t.name} loading="lazy"
                    className="h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    style={{ width: device === "desktop" ? "100%" : device === "tablet" ? "70%" : "42%" }} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-4 gap-2">
                    <span className="px-4 py-2 rounded-full bg-white text-black text-xs font-bold">Preview</span>
                    <Link to="/login" className="px-4 py-2 rounded-full gradient-bg text-white text-xs font-bold">Use Theme</Link>
                  </div>
                </div>
                <div className="p-4 flex items-center justify-between">
                  <div><p className="font-display font-bold text-sm">{t.name}</p><p className="text-xs text-muted-foreground">{t.category}</p></div>
                  <div className="flex gap-1">{Object.values(t.colors || {}).slice(0, 3).map((c, j) => <span key={j} className="h-3.5 w-3.5 rounded-full border border-border" style={{ backgroundColor: c }} />)}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="text-center mt-4">
          <Button data-testid="explore-themes" onClick={() => navigate("/login")} variant="outline" className="rounded-full font-bold glass-card h-12 px-8">
            Explore Themes <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </Reveal>
      </section>

      {/* ===== PRICING ===== */}
      <section id="pricing" className="py-24 gradient-bg-soft">
        <div className="max-w-5xl mx-auto px-6">
          <Reveal className="text-center">
            <p className="overline-label text-gradient">Partner pricing</p>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter mt-3">One simple package, whatever your scale</h2>
            <p className="text-muted-foreground mt-4 max-w-xl mx-auto">Charge your clients whatever you want — MILTROO bills you only for active websites, on a plan that gets cheaper as you grow.</p>
          </Reveal>
          <PricingCards cfg={pricingCfg} onCta={() => navigate("/login")} />
          <Reveal className="mt-10"><PricingCalculator cfg={pricingCfg} /></Reveal>
        </div>
      </section>

      {/* ===== SUPER ADMIN PANEL (educational) ===== */}
      <AdSlot slug="" page="home" slot="after_hero" />

      <SuperAdminSection />

      {/* ===== REVENUE OPPORTUNITY ===== */}
      <section className="py-24 max-w-5xl mx-auto px-6">
        <Reveal className="text-center">
          <p className="overline-label text-gradient">The opportunity</p>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter mt-3">Build more websites. Grow your recurring revenue.</h2>
        </Reveal>
        <Reveal className="mt-12"><RevenueCalculator /></Reveal>
      </section>

      {/* ===== FEATURES ===== */}
      <section id="features" className="py-24 gradient-bg-soft">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="text-center">
            <p className="overline-label text-gradient">Everything included</p>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter mt-3">A complete commerce operating system</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-14">
            {FEATURES.map(({ icon: Icon, title, body }, i) => (
              <Reveal key={title} delay={(i % 3) * 0.07}>
                <div data-testid={`feature-${i}`} className="glass-card rounded-xl p-6 h-full hover:-translate-y-1 hover:glow-sm transition-all duration-300 group">
                  <div className="h-11 w-11 rounded-xl gradient-bg text-white flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-transform">
                    <Icon className="h-5 w-5" />
                  </div>
                  <p className="font-display font-bold text-lg mt-4">{title}</p>
                  <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="py-24 max-w-6xl mx-auto px-6">
        <Reveal>
          <div className="rounded-3xl p-10 sm:p-16 text-center text-white relative overflow-hidden gradient-bg glow">
            <div className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-white/10 animate-float" />
            <div className="absolute -bottom-20 -left-12 h-64 w-64 rounded-full bg-black/10 animate-float-slow" />
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter relative">Ready to launch your website business?</h2>
            <p className="text-white/80 mt-4 max-w-lg mx-auto relative">Join partners building client websites and online stores on MILTROO today.</p>
            <Button data-testid="cta-get-started" onClick={() => navigate("/login")}
              className="mt-8 h-13 px-10 py-3.5 rounded-full font-bold text-base bg-white text-zinc-900 hover:bg-white/90 active:scale-95 transition-transform relative">
              Get Started Free <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </Reveal>
      </section>

      <AdSlot slug="" page="home" slot="footer" />
      <AdNetworkScripts slug="" page="home" />

      {/* ===== FOOTER ===== */}
      <footer className="border-t border-border gradient-bg-soft">
        <div className="max-w-7xl mx-auto px-6 py-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <BrandLogo size="md" />
            <p className="text-sm text-muted-foreground mt-4 leading-relaxed">The partner-powered website & e-commerce platform. Build, launch and grow client businesses.</p>
          </div>
          {[
            ["Platform", [["Marketplace", "/marketplace"], ["M Play", "/videos"], ["Features", "#features"], ["Themes", "#themes"], ["Pricing", "#pricing"], ["How It Works", "#how"]]],
            ["Partners", [["Become a Freelancer", "/join-freelancer"], ["Become a Creator", "/mkt/auth?mode=register&as=creator"], ["Partner Login", "/login"], ["Store Admin Login", "/business-owner/login"], ["Super Admin", "/super-admin/login"]]],
            ["Support", [["Contact", "mailto:support@miltroo.com"], ["Privacy Policy", "/legal/privacy-policy"], ["Terms of Service", "/legal/terms-conditions"], ["Refund Policy", "/legal/refund-policy"]]],
          ].map(([title, links]) => (
            <div key={title}>
              <p className="overline-label mb-4">{title}</p>
              <div className="space-y-2.5">
                {links.map(([l, to]) => (
                  <Link key={l} to={to} className="block text-sm text-muted-foreground hover:text-foreground transition-colors">{l}</Link>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="border-t border-border py-6 text-center text-xs text-muted-foreground">
          © {new Date().getFullYear()} MILTROO. Crafted for partners who build the web.
        </div>
      </footer>
    </div>
  );
}
